import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCardFrontImageSrc } from '../lib/cardImages';
import type { Orientation, TarotCard } from '../lib/types';

type StyleId = 'velvet' | 'grounded' | 'direct';

type StyleProfile = {
  id: StyleId;
  label: string;
  summary: string;
  accent: string;
};

type StyleCard = {
  card: TarotCard;
  orientation: Orientation;
  positionLabel: string;
  transcripts: Record<StyleId, string[]>;
};

const LINE_REVEAL_MS = 850;

const STYLES: StyleProfile[] = [
  {
    id: 'velvet',
    label: 'Velvet Mystic',
    summary: '신비감을 살리되 지나치게 흐리지 않고, 감정의 결을 천천히 조명하는 스타일.',
    accent: 'from-[#d5a462]/30 via-[#a55ef5]/10 to-transparent',
  },
  {
    id: 'grounded',
    label: 'Grounded Reader',
    summary: '실제 상담가처럼 현실 언어로 풀어 주는 스타일. 과장보다 상황 판단에 강하다.',
    accent: 'from-[#d5a462]/30 via-[#4c8f72]/10 to-transparent',
  },
  {
    id: 'direct',
    label: 'Straight Reader',
    summary: '돌려 말하지 않고 바로 짚는 스타일. 카드가 꺼내는 핵심을 짧고 날카롭게 던진다.',
    accent: 'from-[#d5a462]/30 via-[#9d4e45]/10 to-transparent',
  },
];

const STYLE_CARDS: StyleCard[] = [
  {
    card: {
      id: 'wands-06',
      name_en: 'Six of Wands',
      name_kr: '완드 6',
      arcana: 'minor',
      suit: 'wands',
      number: 6,
      keywords_upright: ['인정', '성과', '앞으로 드러남'],
      keywords_reversed: ['인정 욕구', '겉만 번지르르함', '불안한 자의식'],
      meaning_short: '성과와 인정의 장면이 보이지만, 그 뒤에 불안도 같이 비추는 카드',
    },
    orientation: 'upright',
    positionLabel: '겉으로 보이는 흐름',
    transcripts: {
      velvet: [
        '첫 카드가 완드 6이면, 겉으로는 이미 빛이 들어오고 있어. 사람들 눈에는 잘 풀리는 장면처럼 보일 수 있지.',
        '그런데 이 카드가 아름다운 건 승리 때문만이 아니야. 그 빛을 잃을까 봐 더 예민해지는 마음까지 같이 데려오기 때문이야.',
      ],
      grounded: [
        '완드 6은 겉으로 드러나는 성과 카드야. 지금 상황도 누가 봐도 나쁘다고 하긴 어려워.',
        '문제는 여기서 생겨. 결과가 어느 정도 보이기 시작할수록, 오히려 평가받는 압박이 더 커진다는 점이야.',
      ],
      direct: [
        '첫 카드부터 인정 욕구가 보여. 잘되고 싶은 마음이 분명하고, 그만큼 시선도 많이 의식하고 있어.',
        '이건 성과 카드지만 동시에 불안 카드이기도 해. 보여지는 순간 더 쫓기기 시작하거든.',
      ],
    },
  },
  {
    card: {
      id: 'major-13',
      name_en: 'Death',
      name_kr: '죽음',
      arcana: 'major',
      number: 13,
      keywords_upright: ['종결', '구조 변화', '이전 상태의 종료'],
      keywords_reversed: ['질질 끄는 끝맺음', '변화 저항', '정체'],
      meaning_short: '무언가를 끝내지 않으면 다음 장면이 열리지 않는 카드',
    },
    orientation: 'upright',
    positionLabel: '진짜로 지나가야 하는 문턱',
    transcripts: {
      velvet: [
        '죽음이 가운데에 선 순간, 이 리딩은 훨씬 분명해져. 네가 두려운 건 실패보다도, 익숙한 장면을 떠나는 일이야.',
        '이 카드는 잃음만 말하지 않아. 이미 끝난 구조를 계속 붙잡을 때 생기는 피로를 조용히 들춰내지.',
      ],
      grounded: [
        '가운데 죽음은 해석이 복잡하지 않아. 다음 단계로 가려면 지금 구조 하나는 정리해야 한다는 뜻이야.',
        '사람들은 이 카드를 무섭게 보지만 실제 상담에서는 꽤 현실적이야. 오래 끌던 문제를 더 못 미룬다는 신호거든.',
      ],
      direct: [
        '핵심은 이거야. 안 끝낸 걸 끝내야 해.',
        '죽음이 가운데에 서면 미련이 제일 큰 변수야. 감정이 아니라 정리의 타이밍 문제로 읽혀.',
      ],
    },
  },
  {
    card: {
      id: 'swords-ace',
      name_en: 'Ace of Swords',
      name_kr: '소드 에이스',
      arcana: 'minor',
      suit: 'swords',
      number: 1,
      keywords_upright: ['명료함', '결정', '정확한 말'],
      keywords_reversed: ['말의 충돌', '성급한 단정', '미완의 결론'],
      meaning_short: '흐려진 장면을 뚫고 기준을 세워 주는 카드',
    },
    orientation: 'upright',
    positionLabel: '이후에 잡게 될 기준',
    transcripts: {
      velvet: [
        '마지막에 소드 에이스가 놓이면, 감정의 안개를 지나 결국 또렷한 말 한 문장으로 돌아오게 돼.',
        '이 카드는 상처를 남기기 위한 칼이 아니라, 이제는 흐리지 말고 분명히 하라는 기준선에 가까워.',
      ],
      grounded: [
        '결말 카드가 소드 에이스면 정답은 의외로 단순해. 정확한 기준을 세우고, 그 기준대로 한 번 잘라내면 돼.',
        '결국 중요한 건 감정 정리가 아니라 의사결정 문장을 만드는 거야. 무엇을 할지보다 무엇을 더는 안 할지를 정하는 쪽이지.',
      ],
      direct: [
        '마지막은 선명해. 결론은 말로 정리해야 해.',
        '이 판은 결국 기준의 문제야. 마음이 아니라 문장으로 결정을 내려야 끝나.',
      ],
    },
  },
];

export default function ReaderStylesLabPage() {
  const [activeStyle, setActiveStyle] = useState<StyleId>('velvet');
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [visibleLineCount, setVisibleLineCount] = useState(1);

  const currentCard = STYLE_CARDS[currentCardIndex];
  const currentLines = useMemo(() => currentCard.transcripts[activeStyle], [activeStyle, currentCard]);

  useEffect(() => {
    setVisibleLineCount(1);

    const timer = window.setInterval(() => {
      setVisibleLineCount((count) => {
        if (count >= currentLines.length) {
          window.clearInterval(timer);
          return count;
        }

        return count + 1;
      });
    }, LINE_REVEAL_MS);

    return () => window.clearInterval(timer);
  }, [currentLines]);

  const visibleLines = currentLines.slice(0, visibleLineCount);
  const selectedStyle = STYLES.find((style) => style.id === activeStyle)!;

  return (
    <main className="min-h-screen bg-[#07060a] text-[#f7efe4]">
      <div className={`pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(194,147,91,0.18),_transparent_32%),linear-gradient(180deg,_#100c12_0%,_#07060a_52%,_#07060a_100%)]`} />

      <section className="mx-auto max-w-[1480px] px-5 pb-16 pt-20 lg:px-10">
        <div className="flex flex-col gap-4 border-b border-white/10 pb-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#c89b61]">Prototype 03 · Reader Styles</p>
            <h1 className="mt-4 font-['Song_Myung'] text-4xl leading-[1.05] text-[#fff8ed] sm:text-5xl lg:text-6xl">
              같은 카드도
              <br />
              리더의 목소리에 따라 다르게 들린다
            </h1>
            <p className="mt-5 text-sm leading-8 text-[#d7c9b3] sm:text-base">
              Tarovent가 내세우는 `reader style + live streaming interpretation` 구조를 묘안용으로 실험한 페이지입니다.
              브랜드 톤을 바꿀 때 프롬프트보다 먼저, 어떤 리더 목소리가 사용자에게 맞는지 비교하기 좋은 형태입니다.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 text-xs text-[#d9ccb8]">
            <Link to="/lab" className="rounded-full border border-white/15 px-4 py-2 transition hover:border-[#d5a462] hover:text-white">
              실험실 홈
            </Link>
            <Link to="/lab/immersive-reading" className="rounded-full bg-[#f5eee4] px-4 py-2 font-medium text-[#120d11] transition hover:bg-white">
              첫 프로토타입
            </Link>
          </div>
        </div>

        <div className="mt-8 grid gap-8 xl:grid-cols-[0.8fr_1.2fr_0.8fr]">
          <aside className="rounded-[30px] border border-white/10 bg-white/[0.03] p-6">
            <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89b61]">Reader Voice</p>
            <div className="mt-5 space-y-3">
              {STYLES.map((style) => (
                <button
                  key={style.id}
                  type="button"
                  onClick={() => setActiveStyle(style.id)}
                  className={`w-full rounded-[24px] border px-4 py-4 text-left transition ${
                    activeStyle === style.id
                      ? 'border-[#d5a462]/45 bg-[#1a1417] text-white'
                      : 'border-white/8 bg-black/10 text-[#cdbda6]'
                  }`}
                >
                  <span className="block text-[10px] uppercase tracking-[0.34em] text-[#c89b61]">{style.label}</span>
                  <span className="mt-2 block text-sm leading-7">{style.summary}</span>
                </button>
              ))}
            </div>
          </aside>

          <section className="rounded-[30px] border border-white/10 bg-[#120d11]/75 p-6 shadow-[0_40px_110px_rgba(0,0,0,0.34)]">
            <div className={`rounded-[26px] border border-white/8 bg-[linear-gradient(180deg,_rgba(255,255,255,0.04),_rgba(255,255,255,0.01))] p-5`}>
              <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89b61]">Streaming Reading</p>
              <div className="mt-4 flex items-center gap-3">
                <span className="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.28em] text-[#dbc7aa]">
                  {selectedStyle.label}
                </span>
                <span className="text-xs text-[#aa9a86]">
                  {currentCard.positionLabel} · {currentCard.card.name_kr}
                </span>
              </div>

              <div className="mt-6 space-y-3">
                {visibleLines.map((line) => (
                  <article key={line} className="rounded-[22px] border border-white/8 bg-[#171116] px-4 py-4 text-[15px] leading-8 text-[#f1e7d8]">
                    {line}
                  </article>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setCurrentCardIndex((index) => Math.max(index - 1, 0))}
                  disabled={currentCardIndex === 0}
                  className="rounded-full border border-white/12 px-4 py-2 text-sm text-[#f4ede2] transition hover:border-white/30 disabled:cursor-not-allowed disabled:opacity-35"
                >
                  이전 카드
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentCardIndex((index) => Math.min(index + 1, STYLE_CARDS.length - 1))}
                  disabled={currentCardIndex === STYLE_CARDS.length - 1}
                  className="rounded-full bg-[#f5eee4] px-5 py-2 text-sm font-medium text-[#110d11] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                  다음 카드
                </button>
              </div>
            </div>
          </section>

          <aside className="space-y-6">
            <section className="rounded-[30px] border border-white/10 bg-white/[0.03] p-6">
              <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89b61]">Spread Context</p>
              <div className="mt-4 space-y-4">
                {STYLE_CARDS.map((item, index) => (
                  <article
                    key={item.card.id}
                    className={`rounded-2xl border px-4 py-4 transition ${
                      index === currentCardIndex ? 'border-[#d5a462]/40 bg-[#171116]' : 'border-white/8 bg-black/10'
                    }`}
                  >
                    <p className="text-[10px] uppercase tracking-[0.34em] text-[#c89b61]">{item.positionLabel}</p>
                    <div className="mt-3 flex items-center gap-4">
                      <div className="h-16 w-12 overflow-hidden rounded-xl border border-white/10 bg-black/20">
                        <img
                          src={getCardFrontImageSrc(item.card)}
                          alt={item.card.name_kr}
                          className={`h-full w-full object-cover ${item.orientation === 'reversed' ? 'rotate-180' : ''}`}
                        />
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-[#fff6ea]">{item.card.name_kr}</p>
                        <p className="text-sm text-[#ccbca5]">{item.card.meaning_short}</p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <section className="rounded-[30px] border border-[#c89b61]/20 bg-[#171015] p-6">
              <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89b61]">What To Learn</p>
              <ul className="mt-4 space-y-3 text-sm leading-7 text-[#e1d4c3]">
                <li className="rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-4">같은 카드라도 어떤 리더 목소리가 묘안 브랜드에 맞는지 비교할 수 있다.</li>
                <li className="rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-4">실시간 줄단위 스트리밍이 전체 문단보다 더 사람 같은 진행감을 준다.</li>
                <li className="rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-4">스타일 선택은 결국 프롬프트 실험을 위한 사용자 테스트 장치가 될 수 있다.</li>
              </ul>
            </section>
          </aside>
        </div>
      </section>
    </main>
  );
}
